import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@/axios";
import { routes } from "@/utils/urls";

export interface HealthPlanResponse {
  status: number;
  data: {
    memberHealthPlan: string;
    memberStatus: string;
    enrollmentStartDate: string;
    enrollmentEndDate: string;
    eligibilityLastChecked: string;
  };
}

export interface HealthPlanState {
  byMemberId: Record<string, HealthPlanResponse["data"] | undefined>;
  loading: boolean;
  error: string | null;
}

const initialState: HealthPlanState = {
  byMemberId: {},
  loading: false,
  error: null,
};

const HEALTHPLAN_DUMMY: HealthPlanResponse = {
  status: 200,
  data: {
    memberHealthPlan: "HealthFirst Plan",
    memberStatus: "Active",
    enrollmentStartDate: "2024-01-01T00:00:00.000Z",
    enrollmentEndDate: "2025-12-31T00:00:00.000Z",
    eligibilityLastChecked: "2024-10-01T00:00:00.000Z",
  },
};

export const fetchHealthPlan = createAsyncThunk<
  { memberId: string; data: HealthPlanResponse["data"] },
  { memberId: string },
  { rejectValue: string }
>(
  "healthplanByMemberId/fetch",
  async ({ memberId }, { rejectWithValue }) => {
    try {
      try {
        const res = await api.get(`${routes.cards.healthplan}/${memberId}`);
        const data = res.data as HealthPlanResponse;
        if (data && data.data) {
          return { memberId, data: data.data };
        }
      } catch (_e) {
        // fall back to dummy
      }
      return { memberId, data: HEALTHPLAN_DUMMY.data };
    } catch (_e) {
      return rejectWithValue("Failed to fetch health plan");
    }
  }
);

const healthplanSlice = createSlice({
  name: "healthplanByMemberId",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHealthPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.byMemberId[action.payload.memberId] = action.payload.data;
      })
      .addCase(fetchHealthPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch health plan";
      });
  },
});

export default healthplanSlice.reducer;